/**
 * The navigation manifest.
 *
 * One list drives the sidebar, the router and the page headers, so a route can
 * never exist without a way to reach it — or appear in the sidebar and 404.
 *
 * `phase` records which delivery phase replaces the placeholder. It is shown to
 * the user on the placeholder screen, because a shopkeeper evaluating the system
 * deserves to know "not built yet" rather than being left to conclude "broken".
 */

import {
  BarChart3,
  Boxes,
  ChefHat,
  CreditCard,
  Gauge,
  Headset,
  Package,
  ReceiptText,
  ScrollText,
  Settings,
  ShoppingBag,
  ShoppingCart,
  Store,
  Truck,
  Users,
  type LucideIcon,
} from "lucide-react";

export interface NavRoute {
  path: string;
  label: string;
  /** Shown under the page title in the top bar. */
  subtitle: string;
  icon: LucideIcon;
  phase: string;
  /**
   * The permission required to see this in the navigation and open it.
   * Undefined means everyone signed in may reach it.
   */
  permission?: string;
  /**
   * What a SmartSpaza support view needs instead.
   *
   * A support view holds no permission that changes anything, so a screen
   * gated on doing — the till needs `sales.create` — would be closed to the
   * people trying to help with it. This names the view permission that opens
   * it read-only during a support visit, and only then.
   */
  supportPermission?: string;
  /** Which summary panel the sidebar shows while this route is active. */
  summary?: "today" | "stock";
}

export const NAV_ROUTES: readonly NavRoute[] = [
  {
    path: "/dashboard",
    label: "Dashboard",
    subtitle: "Your business at a glance",
    icon: Gauge,
    phase: "delivered",
    // Gated now that it shows takings. A cashier has no use for it and the
    // server refuses the endpoint anyway — an item in the sidebar that 403s is
    // worse than no item.
    permission: "analytics.view",
  },
  {
    path: "/sales",
    label: "Sales",
    subtitle: "Create a new sale and manage transactions",
    icon: ShoppingBag,
    phase: "Phase 4",
    summary: "today",
    permission: "sales.create",
    supportPermission: "sales.view",
  },
  {
    path: "/customers",
    label: "Customers",
    subtitle: "Accounts, purchase history and loyalty",
    icon: Users,
    phase: "delivered",
    permission: "customers.view",
  },
  {
    path: "/inventory",
    label: "Inventory",
    subtitle: "Track stock, manage products and reduce stockouts",
    icon: Package,
    // Products, import and the shared catalogue are delivered. Stock levels,
    // movements and receiving join them on this same screen in Phase 3.
    phase: "Phase 3",
    summary: "stock",
    permission: "inventory.view",
  },
  {
    path: "/marketplace",
    label: "Marketplace",
    subtitle:
      "Buy stock from wholesalers, and buy it together with other shops",
    icon: Store,
    phase: "delivered",
    // Browsing wholesale prices is not the same grant as spending the shop's
    // money — `marketplace.order` is separate, and the server enforces it. This
    // one only decides whether the sidebar shows the item.
    permission: "marketplace.view",
  },
  {
    path: "/suppliers",
    label: "Suppliers",
    subtitle: "Wholesalers, pricing and supplier accounts",
    icon: Truck,
    phase: "delivered",
    permission: "suppliers.view",
  },
  {
    path: "/procurement",
    label: "Procurement",
    subtitle: "Purchase orders and goods receiving",
    icon: ShoppingCart,
    phase: "delivered",
    permission: "procurement.view",
  },
  {
    path: "/reports",
    label: "Reports",
    subtitle: "Sales, cash, inventory and customer reports",
    icon: BarChart3,
    phase: "delivered",
    permission: "reports.view",
  },
  {
    path: "/expenses",
    label: "Expenses",
    subtitle: "Record and categorise business costs",
    icon: ReceiptText,
    phase: "delivered",
    permission: "reports.view",
  },
  {
    path: "/billing",
    label: "Billing",
    subtitle: "Your SmartSpaza package, usage and invoices",
    icon: CreditCard,
    phase: "delivered",
    // The shop's account with SmartSpaza, not the shop's own takings. A cashier
    // has no business here and the server refuses them either way.
    permission: "billing.view",
  },
  {
    path: "/settings",
    label: "Settings",
    subtitle: "Business, users, devices and system rules",
    icon: Settings,
    phase: "delivered",
    permission: "settings.view",
  },
  {
    path: "/support",
    label: "Support",
    subtitle: "Help, diagnostics and contacting SmartSpaza",
    icon: Headset,
    phase: "Phase 11",
  },
] as const;

/**
 * The kitchen queue. Reached from the main navigation rather than a corner,
 * because at a takeaway counter it is somewhere a person works all service —
 * unlike the sync centre, which is a screen you open when something is wrong.
 *
 * Gated on `sales.create`: whoever works the counter takes the orders and
 * whoever works the kitchen has to see them, and a separate permission would be
 * one more thing to grant before a takeaway counter could open.
 */
export const KITCHEN_ROUTE: NavRoute = {
  path: "/kitchen",
  label: "Kitchen",
  subtitle: "Orders waiting to be made",
  icon: ChefHat,
  permission: "sales.create",
  phase: "delivered",
};

/**
 * The sync centre. Not in the supplied designs, and required by the
 * architecture: a cashier must be able to see what has not reached the server,
 * and an owner must be able to review anything that failed. Reached from the
 * status chip rather than the main navigation, since it is a diagnostic screen.
 */
export const SYNC_ROUTE: NavRoute = {
  path: "/sync",
  label: "Sync centre",
  subtitle: "Pending, failed and conflicting transactions",
  icon: Boxes,
  phase: "Phase 6",
};

/**
 * The audit log. Reached from Settings rather than the main navigation: it is
 * something an owner consults when a figure looks wrong, not a screen anybody
 * works in. Guarded by `audit.view`, which only an owner holds by default.
 */
export const AUDIT_ROUTE: NavRoute = {
  path: "/audit",
  label: "Audit log",
  subtitle: "Who did what, and why",
  icon: ScrollText,
  permission: "audit.view",
  phase: "Phase 7",
};

export const ALL_ROUTES: readonly NavRoute[] = [
  ...NAV_ROUTES,
  KITCHEN_ROUTE,
  SYNC_ROUTE,
  AUDIT_ROUTE,
];

export function findRoute(pathname: string): NavRoute | undefined {
  return ALL_ROUTES.find(
    (route) => pathname === route.path || pathname.startsWith(`${route.path}/`),
  );
}

/** The permission that opens `route` now — its support one during a support view. */
export function routePermission(
  route: NavRoute,
  inSupportView: boolean,
): string | undefined {
  return (inSupportView && route.supportPermission) || route.permission;
}
